/**
 * Uniflo brand-placeholder detection for avatars.
 *
 * The Uniflo brand mark / logo / apple-touch defaults are NOT a person's (or a
 * workspace member's) photo. When they leak into a normal person avatar `src`,
 * the avatar must fall back to initials instead of rendering the brand mark.
 * System-specific surfaces that intentionally render the Uniflo assistant
 * should use `systemAvatar` to resolve the platform logo first.
 *
 * Matches:
 *   - the mark/icon/logo SVGs (uniflo-mark, uniflo-icon, uniflo-logo)
 *   - anything served from the bundled brand-asset folder (/uniflo/images/uniflo/)
 *   - the legacy default profile placeholder (default_pic)
 *
 * Deliberately does NOT match a tenant's own uploaded branding (e.g.
 * https://media.uniflo.app/<tenant>/branding/...png) — those are real logos.
 */
const BRAND_AVATAR_PATTERN =
  /(uniflo-mark|uniflo-icon|uniflo-logo|\/uniflo\/images\/uniflo\/|default_pic)/i;

/**
 * @param {string} [value] candidate avatar URL / path
 * @returns {boolean} true when the value is a Uniflo brand placeholder
 */
export function isBrandAvatarAsset(value) {
  const raw = `${value || ""}`.trim();
  return raw ? BRAND_AVATAR_PATTERN.test(raw) : false;
}

/**
 * Returns the given avatar src, or "" when it is a Uniflo brand placeholder
 * (so callers can fall through to an initials/letter avatar).
 *
 * @param {string} [value]
 * @returns {string}
 */
export function sanitizeAvatarSrc(value) {
  const raw = `${value || ""}`.trim();
  if (!raw) return "";
  return isBrandAvatarAsset(raw) ? "" : raw;
}
